import i18next from 'i18next';
import { CUSTOM_TAGS_OPTIONS } from '../Shared/CustomTags';
import permissions from '../../config/permissions';
import { CLAIM_ENTITY_TYPE_SELECTOR_OPTIONS } from '../Claims/constants';
import { INFO_PANE_PAGES } from '../../redux_store';
import FILTER_TYPES from '../Shared/Table/widgets/Filters/constants';

export const ALERTS_TYPE = {
  critical: 'Critical',
  nonCritical: 'Non Critical',
  'monitor & support': 'monitor & support',
};

export const ENTITY_SOURCE_TYPE = {
  program: 'Program',
  assessment: 'Assessment',
  resource: 'Resource',
  intellyChart: 'IntellyChart',
  bhiWebhook: 'BHI Webhook',
};

// Frontend-side fallback list of alert tag values. The authoritative
// source is the backend `/accounts/alerts/tags` endpoint, but new
// tags often appear in alert payloads (`alertTags` array on a row)
// before that endpoint catches up — which then renders as
// "Invalid Data" through `getLocalizedLabel`. This static list is
// merged into the backend options so known tags always resolve;
// once the backend endpoint is updated, the merge is idempotent
// (dedup by value).
//
// The list mirrors the PHQ-GAD16 / Q-Lung alert taxonomy (14 entries
// = 8 Risk Factors + 6 Help-Seeking Behavior items) plus historical
// tags that pre-date the survey to preserve backward compatibility.
export const ALERT_TAGS_OPTIONS = [
  // ---- Risk Factors Identified ----
  { value: 'Q-NEG Positive', label: 'Q-NEG Positive' },
  { value: 'Depression risk detected', label: 'Depression risk detected' },
  { value: 'Anxiety risk detected', label: 'Anxiety risk detected' },
  { value: 'Elevated Allergy Score', label: 'Elevated Allergy Score' },
  { value: 'Poor Asthma Control', label: 'Poor Asthma Control' },
  { value: 'Symptomatic COPD impact score', label: 'Symptomatic COPD impact score' },
  { value: 'High Sleep Disorder Risk', label: 'High Sleep Disorder Risk' },
  { value: 'Tobacco Use', label: 'Tobacco Use' },
  // ---- Help Seeking Behavior ----
  { value: 'Tobacco cessation help requested', label: 'Tobacco cessation help requested' },
  { value: 'Mental Health Help Requested', label: 'Mental Health Help Requested' },
  { value: 'Sleep Help Requested', label: 'Sleep Help Requested' },
  { value: 'Allergy Test Requested', label: 'Allergy Test Requested' },
  { value: 'Interested in Immunotherapy', label: 'Interested in Immunotherapy' },
  { value: 'Lifestyle Help Requested', label: 'Lifestyle Help Requested' },
  // ---- Historical / legacy tags (preserve back-compat) ----
  { value: 'Imminent Risk', label: 'Imminent Risk' },
  { value: 'Suicide or Self-Harm Risk', label: 'Suicide or Self-Harm Risk' },
  { value: 'Psychological distress, no active suicidal ideation', label: 'Psychological distress, no active suicidal ideation' },
  { value: 'Persistent Pain', label: 'Persistent Pain' },
  { value: 'Significant Physical Limitations', label: 'Significant Physical Limitations' },
  { value: 'SDoH Risk', label: 'SDoH Risk' },
  { value: 'Personal Safety Risk', label: 'Personal Safety Risk' },
  { value: 'Preventive Screening Required', label: 'Preventive Screening Required' },
  { value: 'Check A1C', label: 'Check A1C' },
  { value: 'Fall risk', label: 'Fall risk' },
  { value: 'Mental Health', label: 'Mental Health' },
  { value: 'Pain Management', label: 'Pain Management' },
  { value: 'Sleep Quality', label: 'Sleep Quality' },
  { value: 'Lifestyle Improvement', label: 'Lifestyle Improvement' },
  { value: 'Home Sleep Study', label: 'Home Sleep Study' },
  { value: 'Sleep Study Requested', label: 'Sleep Study Requested' },
  { value: 'Pain Management Help Requested', label: 'Pain Management Help Requested' },
  { value: 'Allergy Test', label: 'Allergy Test' },
  { value: 'BHI Consent', label: 'BHI Consent' },
];

export const ALERTS_TYPE_SELECTOR_OPTIONS = [
  {
    label: i18next.t('tables:critical'),
    value: 'critical',
  },
  {
    label: i18next.t('tables:nonCritical'),
    value: 'nonCritical',
  },
  {
    label: i18next.t('tables:monitor&support'),
    value: 'monitor & support',
  },
];

export const STATUS_OPTIONS = [
  {
    label: i18next.t('tables:pendingAcknowledgment'),
    value: 'pendingAcknowledgment',
  },
  {
    label: i18next.t('tables:acknowledged'),
    value: 'acknowledged',
  },
  {
    label: i18next.t('tables:pending'),
    value: 'pending',
  },
];

export const ALERT_CATEGORY_STATUSES = {
  'Imminent Risk': 'Imminent Risk',
  'Help Seeking Behavior': 'Help Seeking Behavior',
  'Risk Factors Identified': 'Risk Factors Identified',
};

export const GET_ALERT_COLUMNS = [
  {
    size: 200,
    header: i18next.t('tables:customTags'),
    accessorKey: 'accountCustomTags',
    permission: permissions.ACCOUNTS_GET,
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: CUSTOM_TAGS_OPTIONS,
    },
    cell: {
      withBubbles: true,
    },
  },
  {
    size: 220,
    header: i18next.t('tables:account'),
    accessorKey: 'accountBusinessName',
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:customId'),
    accessorKey: 'accountCustomIdentifier',
    permission: permissions.ACCOUNTS_GET,
    enableHiding: false,
    filter: {},
    isDefaultCell: true,
  },
  {
    size: 240,
    header: i18next.t('tables:patientFirstName'),
    accessorKey: 'patientFirstName',
    filter: {},
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 240,
    header: i18next.t('tables:patientLastName'),
    accessorKey: 'patientLastName',
    filter: {},
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 200,
    header: i18next.t('tables:patientDOB'),
    accessorKey: 'patientDateOfBirth',
    filter: {
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:mrnNumber'),
    accessorKey: 'patientMrnNumber',
    filter: {},
    cell: {
      infoPanePage: INFO_PANE_PAGES.patient_page,
    },
  },
  {
    size: 250,
    header: i18next.t('tables:entityNameAlert'),
    accessorKey: 'entityName',
    apiKey: 'alerts',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
      apiKey: 'alerts',
    },
    isDefaultCell: true,
  },
  {
    size: 330,
    header: i18next.t('tables:alertTags'),
    accessorKey: 'alertTags',
    filter: {
      filterType: FILTER_TYPES.SELECT,
    },
    cell: {
      withBubbles: true,
    },
  },
  {
    size: 200,
    header: i18next.t('tables:orderDate'),
    accessorKey: 'orderDate',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 230,
    header: i18next.t('tables:appointmentTimestamp'),
    accessorKey: 'appointmentTimestamp',
    filter: {
      shouldRange: true,
      hasNoMaxDate: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 230,
    header: i18next.t('tables:invitedTimestamp'),
    accessorKey: 'invitedTimestamp',
    filter: {
      shouldRange: true,
      hasNoMaxDate: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 200,
    header: i18next.t('tables:viewedTimestamp'),
    accessorKey: 'viewedTimestamp',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 230,
    header: i18next.t('tables:entityType'),
    accessorKey: 'entityType',
    cell: {
      withBubble: true,
    },
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: CLAIM_ENTITY_TYPE_SELECTOR_OPTIONS,
    },
  },
  {
    size: 200,
    header: i18next.t('tables:viewedBy'),
    accessorKey: 'viewedBy',
    filter: {},
    isDefaultCell: true,
  },
  {
    size: 250,
    header: i18next.t('tables:alertType'),
    accessorKey: 'alertType',
    cell: {
      withBubble: true,
    },
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: ALERTS_TYPE_SELECTOR_OPTIONS,
    },
  },
  {
    size: 260,
    header: i18next.t('tables:acknowledgeStatus'),
    accessorKey: 'acknowledgeStatus',
    cell: {
      withBubble: true,
    },
    filter: {
      filterType: FILTER_TYPES.SELECT,
      options: STATUS_OPTIONS,
    },
  },
  {
    size: 250,
    header: i18next.t('tables:acknowledgedBy'),
    accessorKey: 'acknowledgedBy',
    filter: {},
    isDefaultCell: true,
  },
  {
    size: 250,
    header: i18next.t('tables:acknowledgedTimestamp'),
    accessorKey: 'acknowledgedTimestamp',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 250,
    header: i18next.t('tables:dateTriggered'),
    accessorKey: 'createdTimestamp',
    filter: {
      shouldRange: true,
      filterType: FILTER_TYPES.DATE,
    },
    isDefaultCell: true,
  },
  {
    size: 230,
    header: i18next.t('tables:assessmentName'),
    accessorKey: 'entityName',
    filter: {
      filterType: FILTER_TYPES.SELECT_WITH_DYNAMIC_HINT,
    },
    isDefaultCell: true,
  },
];
